/**
 * Bedrock A11y patches.
 *
 * Single entry point for theme-side accessibility post-processing applied without
 * forking upstream Horizon files. Each patch targets a specific axe rule observed
 * on the staging Atlas store. See `docs/a11y-canonical-issues.md` for the rule map.
 *
 * Patches included:
 *  1. Slideshow (`slideshow-slide` + `slideshow-controls`)
 *     - Sets `inert` on slides whose `aria-hidden="true"` (axe `aria-hidden-focus`).
 *     - Mirrors invalid `aria-selected` on buttons inside `slideshow-controls` to
 *       `aria-current` (valid on buttons), then removes `aria-selected`
 *       (axe `aria-allowed-attr`). Custom CSS targets `[aria-current='true']`.
 *  2. Overflow list (`<overflow-list>` shadow `<ul part="list">`)
 *     - Adds `role="listitem"` to slotted non-`<li>` children (axe `list`).
 *  3. Hero media (`img.hero__media`)
 *     - Backfills `alt=""` when the upstream `image_tag` produced no `alt`
 *       attribute because the merchant uploaded the asset without alt text
 *       (axe `image-alt`).
 *  4. Gift card recipient (`gift-card-recipient-form`)
 *     - `aria-controls="recipient-fields"` must reference an element id; assigns a
 *       stable id on `.recipient-fields` and syncs controls (axe `aria-valid-attr-value`).
 *  5. Menu landmarks (`accordion-custom.menu nav.details-content`)
 *     - Gives repeated footer/menu `<nav>` landmarks unique accessible names
 *       (axe `landmark-unique`).
 *  6. Pagination disabled arrows (`.pagination__link--disabled`)
 *     - Removes prohibited accessible-name attributes from non-interactive spans
 *       (axe `aria-prohibited-attr`).
 *  7. Embedded media and linked image / icon blocks
 *     - Backfills iframe `title` values and labels linked logo image blocks and
 *       icon-block links whose merchant images do not provide usable alt text
 *       (axe `frame-title`, `link-name`).
 *  8. Overlay card links (`group-block__link`, `card__link`, `hero__link`)
 *     - Adds `aria-label` to empty overlay links whose visible text lives in sibling
 *       content (axe `link-name`). Derives label from `.text-block.h*` headings, then
 *       semantic headings, image alt, or URL slug.
 *  9. BOGOS volume-discount widget (app-injected markup)
 *     - Adds `aria-label` to `input.bogos-vl-select-variant-title-input` (axe `label`).
 *
 * All patches are re-applied via a single `MutationObserver` so dynamically swapped
 * subtrees (filters re-render, hero swaps, slideshow paging) stay clean. On `childList`
 * mutations, `applyRelevant` uses cheap `matches` / `querySelector` gates (and
 * `closest` only for hosts where new nodes can be inserted under `overflow-list` or
 * `gift-card-recipient-form` without those tags appearing in the added subtree).
 */
(function () {
  'use strict';

  // ---------- Slideshow ----------

  /** @param {Element} el */
  function isSlideshowSlide(el) {
    return el instanceof HTMLElement && el.tagName.toLowerCase() === 'slideshow-slide';
  }

  /** @param {Element} slide */
  function syncSlideInert(slide) {
    if (!isSlideshowSlide(slide)) return;
    const hidden = slide.getAttribute('aria-hidden') === 'true';
    if ('inert' in slide) {
      try {
        /** @type {any} */ (slide).inert = hidden;
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * `aria-selected` is not allowed on plain buttons; upstream slideshow.js sets it on
   * thumbnail/dot controls. Mirror to `aria-current` for styling and screen readers.
   * @param {Element} btn
   */
  function mirrorSlideshowControlSelection(btn) {
    const selected = btn.getAttribute('aria-selected');
    if (selected === 'true') {
      btn.setAttribute('aria-current', 'true');
    } else if (selected === 'false') {
      btn.removeAttribute('aria-current');
    }
    btn.removeAttribute('aria-selected');
  }

  /** @param {ParentNode} root */
  function stripThumbnailAriaSelected(root) {
    const controlsHosts = new Set();
    if (root instanceof Element) {
      const enclosing = root.closest('slideshow-controls');
      if (enclosing) controlsHosts.add(enclosing);
    }
    root.querySelectorAll?.('slideshow-controls').forEach((el) => controlsHosts.add(el));
    controlsHosts.forEach((host) => {
      host.querySelectorAll?.('button[aria-selected]').forEach(mirrorSlideshowControlSelection);
    });
  }

  /** @param {ParentNode} root */
  function patchSlideshow(root) {
    const slides = new Set();
    if (root instanceof Element) {
      const enclosing = root.closest('slideshow-slide');
      if (enclosing) slides.add(enclosing);
    }
    root.querySelectorAll?.('slideshow-slide[aria-hidden]').forEach((slide) => slides.add(slide));
    slides.forEach((slide) => syncSlideInert(slide));
    stripThumbnailAriaSelected(root);
  }

  // ---------- Overflow list ----------

  /**
   * `<overflow-list>` renders a `<ul part="list">` in shadow DOM with a `<slot>` that flattens
   * non-`<li>` children (e.g. `<accordion-custom>` filter facets) directly under the `<ul>`.
   * axe `list` rule then complains that "List element has direct children that are not allowed".
   * Marking each non-`<li>` slotted child with `role="listitem"` satisfies the rule without
   * altering visual or interactive behaviour (the wrapping `<ul>` remains a list).
   *
   * @param {ParentNode} root
   */
  function patchOverflowList(root) {
    const hosts = new Set();
    if (root instanceof HTMLElement && root.tagName.toLowerCase() === 'overflow-list') {
      hosts.add(root);
    }
    root.querySelectorAll?.('overflow-list').forEach((el) => {
      if (el instanceof HTMLElement) hosts.add(el);
    });
    if (root instanceof Element) {
      const enclosing = root.closest('overflow-list');
      if (enclosing instanceof HTMLElement) hosts.add(enclosing);
    }
    hosts.forEach((host) => {
      Array.from(host.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) return;
        if (child.tagName === 'LI') return;
        if (child.getAttribute('slot') === 'overflow') return;
        if (!child.hasAttribute('role')) {
          child.setAttribute('role', 'listitem');
        }
      });
    });
  }

  // ---------- Hero media ----------

  /**
   * Some upstream Horizon image renderers (e.g. `sections/hero.liquid`) pass `alt: image.alt`
   * straight through. When the merchant uploads an image without alt text, Liquid's
   * `image_tag` filter emits the `<img>` with no `alt` attribute, which fails axe `image-alt`.
   * Backfilling `alt=""` on theme-owned media that's clearly decorative-by-omission keeps the
   * rule happy without inventing copy. Scope: explicit known classes - do not rewrite
   * arbitrary `<img>` tags.
   */
  const DECORATIVE_IMG_SELECTORS = ['img.hero__media:not([alt])'];
  const DECORATIVE_IMG_GATE_SELECTOR = DECORATIVE_IMG_SELECTORS.join(',');

  /** @param {ParentNode} root */
  function patchDecorativeImageAlt(root) {
    DECORATIVE_IMG_SELECTORS.forEach((selector) => {
      root.querySelectorAll?.(selector).forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        if (!img.hasAttribute('alt')) {
          img.setAttribute('alt', '');
        }
      });
    });
  }

  // ---------- Gift card recipient (aria-controls target) ----------

  let nextRecipientFieldsId = 0;

  /**
   * Gift card radios use aria-controls="recipient-fields" but the region often has no id.
   * @param {ParentNode} root
   */
  function patchGiftCardRecipientAriaControls(root) {
    const forms = new Set();
    if (root instanceof HTMLElement && root.matches('gift-card-recipient-form')) {
      forms.add(root);
    }
    root.querySelectorAll?.('gift-card-recipient-form').forEach((el) => {
      if (el instanceof HTMLElement) forms.add(el);
    });
    if (root instanceof Element) {
      const enclosing = root.closest('gift-card-recipient-form');
      if (enclosing instanceof HTMLElement) forms.add(enclosing);
    }

    forms.forEach((form) => {
      const region = form.querySelector('.recipient-fields');
      if (!(region instanceof HTMLElement)) return;

      if (!region.id || region.id.length === 0) {
        region.id = `recipient-fields-${nextRecipientFieldsId++}`;
      }
      const id = region.id;

      for (const control of form.querySelectorAll('[aria-controls="recipient-fields"]')) {
        if (!(control instanceof Element)) continue;
        control.setAttribute('aria-controls', id);
      }
    });
  }

  // ---------- Menu landmarks ----------

  /** @param {string | null | undefined} value */
  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Footer menu blocks render multiple `<nav class="details-content">` landmarks.
   * The visual summary is enough for sighted users, but the nav itself needs a
   * unique accessible name for axe. Run against `document` so labels stay unique
   * across all menu blocks, even when a single menu subtree is re-rendered.
   */
  function patchMenuLandmarkLabels() {
    const navs = Array.from(
      document.querySelectorAll('accordion-custom.menu nav.details-content, .menu nav.details-content')
    ).filter(
      /** @param {Element} nav */
      (nav) => nav instanceof HTMLElement
    );

    const labelCounts = new Map();

    navs.forEach(
      /** @param {HTMLElement} nav */
      (nav) => {
        const menu = nav.closest('.menu') || nav.closest('accordion-custom');
        const summary = menu?.querySelector('summary');
        const summaryDefault = summary?.querySelector('.menu__heading__default');
        const summaryAccordion = summary?.querySelector('.menu__heading__accordion');
        const existing = normalizeText(nav.getAttribute('aria-label'));
        const visibleHeading =
          normalizeText(summaryDefault?.textContent) ||
          normalizeText(summaryAccordion?.textContent) ||
          normalizeText(summary?.textContent);
        const firstLink = normalizeText(nav.querySelector('a')?.textContent);
        const baseLabel = existing || visibleHeading || firstLink || 'Menu';
        const contextualLabel = nav.closest('footer') ? `${baseLabel} footer menu` : `${baseLabel} menu`;
        const count = labelCounts.get(contextualLabel) || 0;

        labelCounts.set(contextualLabel, count + 1);
        nav.setAttribute('aria-label', count === 0 ? contextualLabel : `${contextualLabel} ${count + 1}`);
      }
    );
  }

  // ---------- Pagination ----------

  /** @param {ParentNode} root */
  function patchPaginationDisabledArrows(root) {
    const controls = new Set();
    if (root instanceof HTMLElement && root.matches('.pagination__link--disabled')) {
      controls.add(root);
    }
    root.querySelectorAll?.('.pagination__link--disabled').forEach(
      /** @param {Element} control */
      (control) => controls.add(control)
    );

    controls.forEach(
      /** @param {Element} control */
      (control) => {
        if (!(control instanceof HTMLElement) || control.tagName !== 'SPAN') return;
        control.removeAttribute('aria-label');
        control.removeAttribute('aria-disabled');
        control.setAttribute('aria-hidden', 'true');
      }
    );
  }

  // ---------- Embedded media / linked images ----------

  /** @param {HTMLIFrameElement} iframe */
  function getIframeTitle(iframe) {
    const src = iframe.getAttribute('src') || '';

    if (iframe.classList.contains('js-youtube') || src.includes('youtube.com/embed')) {
      return 'Embedded YouTube video';
    }
    if (iframe.classList.contains('js-vimeo') || src.includes('player.vimeo.com/video')) {
      return 'Embedded Vimeo video';
    }
    if (src.includes('google.com/maps/embed')) {
      return 'Bundaberg Rum Distillery location map';
    }

    return '';
  }

  /** @param {ParentNode} root */
  function patchIframeTitles(root) {
    const iframes = new Set();
    if (root instanceof HTMLIFrameElement) {
      iframes.add(root);
    }
    root.querySelectorAll?.('iframe').forEach(
      /** @param {Element} iframe */
      (iframe) => iframes.add(iframe)
    );

    iframes.forEach(
      /** @param {Element} iframe */
      (iframe) => {
        if (!(iframe instanceof HTMLIFrameElement)) return;
        if (normalizeText(iframe.getAttribute('title'))) return;

        const title = getIframeTitle(iframe);
        if (title) {
          iframe.setAttribute('title', title);
        }
      }
    );
  }

  const LINKED_IMAGE_SELECTORS = ['a.image-block[href]', '.icon-block a[href]'];
  const LINKED_IMAGE_GATE_SELECTOR = LINKED_IMAGE_SELECTORS.join(',');

  const LINKED_IMAGE_LABELS = [
    {
      hrefIncludes: 'drinkwise.org.au',
      label: 'DrinkWise',
    },
    {
      hrefIncludes: 'harrybrown.com.au/promotions/bundy-follow-the-footy',
      label: 'Harry Brown Follow the Footy promotion',
    },
    {
      hrefIncludes: 'sipnsave.com.au/promotions/bundy-follow-the-footy',
      label: 'SipnSave Follow the Footy promotion',
    },
    {
      hrefIncludes: 'bottlemart.com.au/promotions/bundy-follow-the-footy',
      label: 'Bottlemart Follow the Footy promotion',
    },
    {
      hrefIncludes: 'thirstycamel.com.au/promotions/bundy-follow-the-footy',
      label: 'Thirsty Camel Follow the Footy promotion',
    },
  ];

  /** @param {HTMLAnchorElement} link */
  function getLinkedImageLabel(link) {
    const imageAlt = normalizeText(link.querySelector('img')?.getAttribute('alt'));
    if (imageAlt) return imageAlt;

    const container = link.parentElement;
    const headingText = normalizeText(
      container?.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]')?.textContent
    );
    if (headingText) return `Open product page: ${headingText}`;

    const matchingLabel = LINKED_IMAGE_LABELS.find((entry) => link.href.includes(entry.hrefIncludes));
    if (matchingLabel) return matchingLabel.label;

    try {
      const hostname = new URL(link.href).hostname.replace(/^www\./, '');
      if (hostname) return `Open link to ${hostname}`;
    } catch {
      /* ignore invalid URL */
    }

    return 'Open linked image';
  }

  /** @param {ParentNode} root */
  function patchLinkedImageLabels(root) {
    const links = new Set();
    if (root instanceof HTMLAnchorElement) {
      LINKED_IMAGE_SELECTORS.forEach((selector) => {
        if (root.matches(selector)) links.add(root);
      });
    }
    LINKED_IMAGE_SELECTORS.forEach((selector) => {
      root.querySelectorAll?.(selector).forEach(
        /** @param {Element} link */
        (link) => links.add(link)
      );
    });

    links.forEach(
      /** @param {Element} link */
      (link) => {
        if (!(link instanceof HTMLAnchorElement)) return;
        if (normalizeText(link.textContent) || normalizeText(link.getAttribute('aria-label'))) return;

        link.setAttribute('aria-label', getLinkedImageLabel(link));
      }
    );
  }

  // ---------- Overlay card links ----------

  /** axe `link-name`: empty overlay links in group/card/hero blocks */
  const OVERLAY_LINK_SELECTOR = 'a.group-block__link[href], a.card__link[href], a.hero__link[href]';

  /**
   * Horizon renders full-card overlay links as empty `<a>` siblings ahead of content.
   * Visible headings live in `.group-block-content`, `.card__content`, or
   * `.hero__content-wrapper` and do not contribute to the link accessible name.
   *
   * @param {HTMLAnchorElement} link
   */
  function getOverlayLinkLabel(link) {
    const container = link.parentElement;
    const contentRoot =
      container?.querySelector('.group-block-content, .card__content, .hero__content-wrapper') || container;

    const presetHeading = normalizeText(
      contentRoot?.querySelector(
        '.text-block.h1, .text-block.h2, .text-block.h3, .text-block.h4, .text-block.h5, .text-block.h6'
      )?.textContent
    );
    if (presetHeading) return presetHeading;

    const headingText = normalizeText(
      contentRoot?.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]')?.textContent
    );
    if (headingText) return headingText;

    const imageAlt = normalizeText(contentRoot?.querySelector('img[alt]:not([alt=""])')?.getAttribute('alt'));
    if (imageAlt) return imageAlt;

    try {
      const segment = new URL(link.href, globalThis.location.origin).pathname.split('/').findLast(Boolean);
      if (segment) {
        return segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    } catch {
      /* ignore invalid URL */
    }

    return 'Open link';
  }

  /** @param {ParentNode} root */
  function patchOverlayLinkLabels(root) {
    const links = new Set();
    if (root instanceof HTMLAnchorElement && root.matches(OVERLAY_LINK_SELECTOR)) {
      links.add(root);
    }
    root.querySelectorAll?.(OVERLAY_LINK_SELECTOR).forEach(
      /** @param {Element} link */
      (link) => links.add(link)
    );

    links.forEach(
      /** @param {Element} link */
      (link) => {
        if (!(link instanceof HTMLAnchorElement)) return;
        if (normalizeText(link.textContent) || normalizeText(link.getAttribute('aria-label'))) return;

        link.setAttribute('aria-label', getOverlayLinkLabel(link));
      }
    );
  }

  // ---------- BOGOS volume discount (app-injected) ----------

  /** CI axe failures: `input.bogos-vl-select-variant-title-input[name="bogos-vl-select-variant-input"]` */
  const BOGOS_WIDGET_SELECTOR = '.bogos-volume-discount-widget';
  const BOGOS_VARIANT_INPUT_SELECTOR =
    'input.bogos-vl-select-variant-title-input[name="bogos-vl-select-variant-input"]';

  /**
   * BOGOS injects readonly variant title inputs without `<label>` or ARIA names.
   * @param {HTMLInputElement} input
   */
  function bogosInputHasAccessibleName(input) {
    if (normalizeText(input.getAttribute('aria-label'))) return true;

    const labelledBy = input.getAttribute('aria-labelledby');
    if (labelledBy) {
      const ids = labelledBy.split(/\s+/).filter(Boolean);
      if (
        ids.some((id) => {
          const el = document.getElementById(id);
          return el && normalizeText(el.textContent);
        })
      ) {
        return true;
      }
    }

    if (input.id) {
      const forLabel = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (forLabel && normalizeText(forLabel.textContent)) return true;
    }

    const wrappingLabel = input.closest('label');
    if (wrappingLabel && normalizeText(wrappingLabel.textContent)) return true;

    if (normalizeText(input.getAttribute('placeholder'))) return true;

    return false;
  }

  /**
   * @param {HTMLInputElement} input
   */
  function getBogosVariantInputAriaLabel(input) {
    const itemContainer = input.closest('.bogos-volume-discount_item-container');
    const tierTitle = normalizeText(itemContainer?.querySelector('.bogos-volume-discount-title')?.textContent);
    const widget = input.closest(BOGOS_WIDGET_SELECTOR);
    const widgetTitle = normalizeText(widget?.querySelector('.bogos-volume-discount-widget-title')?.textContent);
    const value = normalizeText(input.value);

    const context = tierTitle || widgetTitle;
    const base = context ? `${context}, selected variant` : 'Selected variant';
    return value ? `${base}: ${value}` : base;
  }

  /** @param {ParentNode} root */
  function patchBogosFormLabels(root) {
    const inputs = new Set();

    if (root instanceof HTMLInputElement && root.matches(BOGOS_VARIANT_INPUT_SELECTOR)) {
      inputs.add(root);
    }

    root.querySelectorAll?.(BOGOS_VARIANT_INPUT_SELECTOR).forEach(
      /** @param {Element} input */
      (input) => {
        if (input instanceof HTMLInputElement) inputs.add(input);
      }
    );

    if (root instanceof Element) {
      const enclosingWidget = root.closest(BOGOS_WIDGET_SELECTOR);
      if (enclosingWidget) {
        enclosingWidget.querySelectorAll(BOGOS_VARIANT_INPUT_SELECTOR).forEach(
          /** @param {Element} input */
          (input) => {
            if (input instanceof HTMLInputElement) inputs.add(input);
          }
        );
      }
    }

    inputs.forEach(
      /** @param {HTMLInputElement} input */
      (input) => {
        if (bogosInputHasAccessibleName(input)) return;

        const label = getBogosVariantInputAriaLabel(input);
        if (!label) return;

        input.setAttribute('aria-label', label);
      }
    );
  }

  // ---------- Orchestration ----------

  /**
   * @param {Element} root
   * @param {string} selector
   */
  function matchesOrContains(root, selector) {
    return Boolean(root.matches(selector) || root.querySelector(selector));
  }

  /** @param {Element} root */
  function applyRelevant(root) {
    if (
      matchesOrContains(root, 'slideshow-slide[aria-hidden]') ||
      matchesOrContains(root, 'slideshow-controls button[aria-selected]')
    ) {
      patchSlideshow(root);
    }

    if (matchesOrContains(root, 'overflow-list') || root.closest('overflow-list')) {
      patchOverflowList(root);
    }

    if (matchesOrContains(root, DECORATIVE_IMG_GATE_SELECTOR)) {
      patchDecorativeImageAlt(root);
    }

    if (matchesOrContains(root, 'gift-card-recipient-form') || root.closest('gift-card-recipient-form')) {
      patchGiftCardRecipientAriaControls(root);
    }

    if (matchesOrContains(root, '.menu nav.details-content') || root.closest('.menu')) {
      patchMenuLandmarkLabels();
    }

    if (matchesOrContains(root, '.pagination__link--disabled')) {
      patchPaginationDisabledArrows(root);
    }

    if (matchesOrContains(root, 'iframe')) {
      patchIframeTitles(root);
    }

    if (matchesOrContains(root, LINKED_IMAGE_GATE_SELECTOR)) {
      patchLinkedImageLabels(root);
    }

    if (matchesOrContains(root, OVERLAY_LINK_SELECTOR)) {
      patchOverlayLinkLabels(root);
    }

    if (
      matchesOrContains(root, BOGOS_VARIANT_INPUT_SELECTOR) ||
      matchesOrContains(root, BOGOS_WIDGET_SELECTOR) ||
      (root instanceof Element && root.closest(BOGOS_WIDGET_SELECTOR))
    ) {
      patchBogosFormLabels(root);
    }
  }

  /** @param {ParentNode} root */
  function applyAll(root) {
    patchSlideshow(root);
    patchOverflowList(root);
    patchDecorativeImageAlt(root);
    patchGiftCardRecipientAriaControls(root);
    patchMenuLandmarkLabels();
    patchPaginationDisabledArrows(root);
    patchIframeTitles(root);
    patchLinkedImageLabels(root);
    patchOverlayLinkLabels(root);
    patchBogosFormLabels(root);
  }

  function handleAttributeMutation(mutation) {
    const target = mutation.target;
    if (!(target instanceof Element)) return;

    if (mutation.attributeName === 'aria-hidden' && isSlideshowSlide(target)) {
      syncSlideInert(target);
    }

    if (
      mutation.attributeName === 'aria-selected' &&
      target instanceof HTMLElement &&
      target.closest('slideshow-controls')
    ) {
      mirrorSlideshowControlSelection(target);
    }
  }

  function handleChildListMutation(mutation) {
    // Gated patches only (`matchesOrContains` / `closest` per feature), not `applyAll`.
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) {
        applyRelevant(node);
      }
    });
  }

  function handleMutation(mutation) {
    if (mutation.type === 'attributes') {
      handleAttributeMutation(mutation);
      return;
    }

    if (mutation.type === 'childList') {
      handleChildListMutation(mutation);
    }
  }

  function init() {
    // Full pass once at startup; mutations below use `applyRelevant` only.
    applyAll(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(handleMutation);
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-selected'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
